import { useTranslation } from 'react-i18next';
import { marked } from 'marked';
import xss from 'xss';
import { BoundedContent, Box, Column, FlexBox, GridContainer, H2, Row } from '@vp/swan';
import EditWrapper from '../../../../components/EditWrapper/EditWrapper';
import FAQAccordion from '../../../../components/FAQAccordion/FAQAccordion';
import { sanitiseLocale } from '../../../../i18n/config';
import { useTrackNavigationClicked } from '../../../../utils/analytics';
import { markdownToSanitizedText } from '../../../../utils/markdownToSanitizedText';
import { logomakerCtaPathByLocale, proLogoDesignUrl } from '../../../../utils/urls';
import LogomakerCTA from '../LogomakerCTA/LogomakerCTA';
import type { FAQ, FAQSchema } from '../../../../components/FAQAccordion/FAQAccordion';

const Faq = () => {
    const { t, i18n } = useTranslation();
    const locale = sanitiseLocale(i18n.language);

    const faq2AnswerLearnMoreLink = proLogoDesignUrl(locale);
    const faq9AnswerLearnMoreLink = proLogoDesignUrl(locale);

    // TODO: This Contentful entry for fr-BE returns 404 for /conditions-generales as it omits the /fr subpath
    // Temporarily hard-coding this path until resolved with the Knights team
    // See: https://vistaprint.slack.com/archives/CFR8FGZ4Y/p1739491781628619
    const faq5AnswerTermsAndConditionsLink =
        locale === 'fr-BE'
            ? '/fr/conditions-generales'
            : t('Site Footer > Terms of Use.contentfulUrl');

    const faqData: FAQ[] = [
        {
            id: 'faq-1',
            question: t('Logomaker landing page > faq 1.question'),
            answer: t('Logomaker landing page > faq 1.answer'),
        },
        {
            id: 'faq-2',
            question: t('Logomaker landing page > faq 2.question'),
            answer: t('Logomaker landing page > faq 2.answer', { faq2AnswerLearnMoreLink }),
        },
        {
            id: 'faq-3',
            question: t('Logomaker landing page > faq 3.question'),
            answer: t('Logomaker landing page > faq 3.answer'),
        },
        {
            id: 'faq-4',
            question: t('Logomaker landing page > faq 4.question'),
            answer: t('Logomaker landing page > faq 4.answer'),
        },
        {
            id: 'faq-5',
            question: t('Logomaker landing page > faq 5.question'),
            answer: t('Logomaker landing page > faq 5.answer', {
                faq5AnswerTermsAndConditionsLink,
            }),
        },
        {
            id: 'faq-6',
            question: t('Logomaker landing page > faq 6.question'),
            answer: t('Logomaker landing page > faq 6.answer'),
        },
        {
            id: 'faq-7',
            question: t('Logomaker landing page > faq 7.question'),
            answer: t('Logomaker landing page > faq 7.answer'),
        },
        {
            id: 'faq-8',
            question: t('Logomaker landing page > faq 8.question'),
            answer: t('Logomaker landing page > faq 8.answer'),
        },
        {
            id: 'faq-9',
            question: t('Logomaker landing page > faq 9.question'),
            answer: t('Logomaker landing page > faq 9.answer', { faq9AnswerLearnMoreLink }),
        },
        {
            id: 'faq-10',
            question: t('Logomaker landing page > faq 10.question'),
            answer: t('Logomaker landing page > faq 10.answer'),
        },
    ];

    const convertMarkdownToHTML = (faqData: FAQ[]): FAQ[] =>
        faqData.map((faq) => ({
            ...faq,
            answer: (
                <div
                    dangerouslySetInnerHTML={{
                        __html: xss(marked.parse(faq.answer as string) as string),
                    }}
                />
            ),
        }));

    const faqSchemaData: FAQSchema[] = faqData.map((faq) => ({
        question: markdownToSanitizedText(faq.question as string),
        answer: markdownToSanitizedText(faq.answer as string),
    }));

    const logomakerCTARef = useTrackNavigationClicked(
        'Design Services',
        'FAQ section CTA',
        logomakerCtaPathByLocale(locale),
        'logoMaker',
    );

    return (
        <BoundedContent marginY="between-sections">
            <GridContainer>
                <Row>
                    <Column span={12} padding={0}>
                        <EditWrapper
                            url={t('Logomaker landing page > faq section heading.editUrl')}
                        >
                            <H2 fontSkin="title-section">
                                {t('Logomaker landing page > faq section heading.value')}
                            </H2>
                        </EditWrapper>
                        <Box marginTop="between-subsections" marginBottom="to-actions">
                            <FAQAccordion
                                faqData={convertMarkdownToHTML(faqData)}
                                faqSchemaData={faqSchemaData}
                            />
                        </Box>
                        <FlexBox justifyContent="center">
                            <LogomakerCTA ctaRef={logomakerCTARef} />
                        </FlexBox>
                    </Column>
                </Row>
            </GridContainer>
        </BoundedContent>
    );
};

export default Faq;
